/**
 * Adjuntos de tareas: tipos de archivo permitidos, límites y esquemas de entrada y respuesta.
 *
 * Los archivos se suben como cuerpo binario crudo (sin multipart):
 *   POST /api/tareas/:id/adjuntos?nombre=foto.png   (Content-Type: image/png, cuerpo = el archivo)
 * La API valida el tipo con esta lista blanca Y revisa los primeros bytes del archivo.
 * SVG y HTML están prohibidos a propósito: pueden ejecutar scripts si alguien los abre.
 */
import { z } from 'zod';
import { fechaIsoSchema } from './comunes';
import { usuarioResumenSchema } from './usuarios';

/** 10 MB por archivo. */
export const TAMANO_MAXIMO_ADJUNTO = 10 * 1024 * 1024;
/** Archivos activos (no eliminados) por tarea. */
export const MAXIMO_ADJUNTOS_POR_TAREA = 20;

/** Tipos permitidos (lista blanca) y las extensiones que corresponden a cada uno. */
export const MIME_PERMITIDOS = {
  'image/png': { extensiones: ['png'], etiqueta: 'Imagen PNG' },
  'image/jpeg': { extensiones: ['jpg', 'jpeg'], etiqueta: 'Imagen JPEG' },
  'image/gif': { extensiones: ['gif'], etiqueta: 'Imagen GIF' },
  'image/webp': { extensiones: ['webp'], etiqueta: 'Imagen WebP' },
  'application/pdf': { extensiones: ['pdf'], etiqueta: 'PDF' },
  'text/plain': { extensiones: ['txt'], etiqueta: 'Texto' },
  'text/csv': { extensiones: ['csv'], etiqueta: 'CSV' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    extensiones: ['docx'],
    etiqueta: 'Word (docx)',
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    extensiones: ['xlsx'],
    etiqueta: 'Excel (xlsx)',
  },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    extensiones: ['pptx'],
    etiqueta: 'PowerPoint (pptx)',
  },
} as const;
export type MimePermitido = keyof typeof MIME_PERMITIDOS;

export const LISTA_MIME_PERMITIDOS = Object.keys(MIME_PERMITIDOS) as MimePermitido[];

/** Solo las imágenes pueden verse dentro de la página (`?inline=1`); el resto siempre se descarga. */
export const MIME_VISIBLES_EN_LINEA: readonly MimePermitido[] = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

export function esMimePermitido(valor: string): valor is MimePermitido {
  return Object.hasOwn(MIME_PERMITIDOS, valor);
}

/* ----------------------------- Entradas ----------------------------- */

/** Parámetros de la URL al subir: `?nombre=foto.png`. */
export const subirAdjuntoConsultaSchema = z.object({
  nombre: z.string().trim().min(1, 'Indica el nombre del archivo.').max(255),
});
export type SubirAdjuntoConsulta = z.infer<typeof subirAdjuntoConsultaSchema>;

/** Parámetros de la URL al descargar: `?inline=1` muestra la imagen en la página. */
export const descargarAdjuntoConsultaSchema = z.object({
  inline: z
    .enum(['1', 'true'])
    .transform(() => true)
    .optional(),
});
export type DescargarAdjuntoConsulta = z.infer<typeof descargarAdjuntoConsultaSchema>;

/* ---------------------------- Respuestas ---------------------------- */

export const adjuntoSchema = z.object({
  id: z.number().int(),
  tareaId: z.number().int(),
  /** Nombre para mostrar (la API ya lo limpió). */
  nombre: z.string(),
  tipoMime: z.string(),
  /** Tamaño en bytes. */
  tamano: z.number().int(),
  subidoPor: usuarioResumenSchema.nullable(),
  creadoEn: fechaIsoSchema,
});
export type Adjunto = z.infer<typeof adjuntoSchema>;

export const listaAdjuntosSchema = z.object({
  items: z.array(adjuntoSchema),
  /** Cuántos más se pueden subir a esta tarea. */
  disponibles: z.number().int(),
});
export type ListaAdjuntos = z.infer<typeof listaAdjuntosSchema>;

/** Códigos de error propios de los adjuntos (para que la web muestre el mensaje adecuado). */
export const CODIGOS_ERROR_ADJUNTOS = {
  tipoNoPermitido: 'adjunto_tipo_no_permitido', // 415
  contenidoInvalido: 'adjunto_contenido_invalido', // 415
  demasiadoGrande: 'adjunto_demasiado_grande', // 413
  vacio: 'adjunto_vacio', // 400
  limite: 'adjunto_limite_alcanzado', // 409
} as const;
